import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import { Link, SpacerHorizontal, SpacerVertical } from '@klarna/bubble-ui';
import { defaultContainerPropTypes, HorizontalWrapper, moetPropTypes } from 'moet-core';

import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { CY_STAKEHOLDER_CONTAINER_IDS } from '@/constants/stakeholders';
import { CAPS } from '@/constants';
import {
  STAKEHOLDER_ROLES_V2,
  stakeholderUtils,
  withStakeholderLogic,
} from '@/containers/withStakeholderLogic';
import { getMerchantAwareTranslator, getMerchantAwareArrayTranslator } from '@/selectors/merchant';
import {
  BackButton,
  ContainerTemplate,
  CallToActionButton,
  StakeholderFormCard,
} from '@/components';
import { logEvent } from '@/utils/analytics';

import { ANALYTICS_EVENTS } from '../analyticsEvents';

const tPrefix = 'products-fe.containers.artificial_ultimate_beneficial_owner';

export class DefaultCeo extends React.Component {
  constructor(props) {
    super(props);
    this.onNext = this.onNext.bind(this);
  }

  onNext() {
    const { mid, onNextWrapper, stakeholders, roleConstraints } = this.props;
    onNextWrapper(
      {
        payload: {
          params: {
            mid,
          },
          body: stakeholderUtils.getStakeholdersMappedValues(stakeholders),
        },
      },
      stakeholderUtils.isStakeholderConstraintsValid(stakeholders, roleConstraints) &&
        stakeholderUtils.isStakeholdersValid(stakeholders),
    );
  }

  render() {
    const {
      availableRoles = [],
      error,
      hasBack,
      isDisabled,
      isLoading,
      onBack,
      onBlurStakeholder,
      onChangeStakeholder,
      onTouchPersonalId,
      roleConstraints,
      stakeholders,
      t,
      tMerchantAware,
      tArrayMerchantAware,
    } = this.props;
    return (
      <ContainerTemplate
        title={tMerchantAware(`${tPrefix}.title`)}
        subtitle={tArrayMerchantAware(`${tPrefix}.subtitle`).map(({ text, link }, index) =>
          link ? (
            <Link key={index} href={link} target="_blank">
              {text}
            </Link>
          ) : (
            <span key={index}>{text}</span>
          ),
        )}
        error={error}
        info={tMerchantAware(`${tPrefix}.infobox`)}
      >
        {stakeholders.map((stakeholder, index) => {
          return (
            <React.Fragment key={`ceo-${stakeholder.reactFormId}`}>
              <SpacerVertical spaceToken="space/150" />
              <StakeholderFormCard
                autoFocus
                tabIndexStart={1}
                stakeholder={stakeholder}
                availableRoles={availableRoles}
                disabledRoles={stakeholderUtils.getDisabledRoles(
                  roleConstraints,
                  stakeholders,
                  stakeholder.roles,
                )}
                onBlur={key => onBlurStakeholder(index, key)}
                onChange={(key, value) => onChangeStakeholder(index, key, value)}
                onTouchPersonalId={() => onTouchPersonalId(index)}
                isDisabled={isDisabled}
                name={`ceo-${index}`}
              />
              <SpacerVertical spaceToken="space/600" />
            </React.Fragment>
          );
        })}
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
          {hasBack && (
            <>
              <BackButton tabIndex={10} onClick={onBack} disabled={isDisabled}>
                {t(`${tPrefix}.buttons.back`)}
              </BackButton>
              <SpacerHorizontal spaceToken="space/300" />
            </>
          )}
          <CallToActionButton
            tabIndex={9}
            onClick={this.onNext}
            disabled={isDisabled}
            loading={isLoading}
            {...generateCyElemId(CY_STAKEHOLDER_CONTAINER_IDS.BTN_SUBMIT, window)}
          >
            {t(`${tPrefix}.buttons.next`)}
          </CallToActionButton>
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}

DefaultCeo.propTypes = {
  ...defaultContainerPropTypes,
  ...moetPropTypes.defaultStakeholderContainerPropTypes,
  merchantCountry: PropTypes.string.isRequired,
  mid: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  tArrayMerchantAware: PropTypes.func.isRequired,
  tMerchantAware: PropTypes.func.isRequired,
};

const WrappedComponent = compose(
  connect((state, { country }) => ({
    merchantCountry: country,
    tMerchantAware: getMerchantAwareTranslator(state),
    tArrayMerchantAware: getMerchantAwareArrayTranslator(state),
  })),
  withStakeholderLogic(CAPS.V2_MERCHANTS_STAKEHOLDERS_PUT, {
    capabilityCallback: ({ payload }) => {
      // rest of the data includes passport number and birthday
      let { type, roles } = payload.body[0];
      roles = roles.join(',');
      logEvent(ANALYTICS_EVENTS.submitCeo, { type, roles });
    },
  }),
)(DefaultCeo);

WrappedComponent.onboardingTypes = '*';
WrappedComponent.deploymentRegionsAndCountries = {
  us: ['fallback'],
  eu: ['fallback'],
  ap: ['fallback'],
};

WrappedComponent.fieldsToFill = [
  {
    fieldName: 'stakeholders',
    roles: [STAKEHOLDER_ROLES_V2.CONTROL_OVER_MANAGEMENT],
  },
];
export default WrappedComponent;
